import type { PlayerSnapshot } from "../types";

// ---------------------------------------------------------------------------
// Ghost replay: record the player's best route run as a lightweight pose track
// and play it back as a translucent "ghost" jet so pilots can race their own
// best time. Storage is keyed off the same fingerprint used for best-time
// tracking, so a ghost always matches the exact ring layout it was set on.
// ---------------------------------------------------------------------------

export interface GhostFrame {
  /** Seconds elapsed since the run started. */
  t: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface GhostRecording {
  key: string;
  seconds: number;
  collected: number;
  recordedAt: string;
  frames: GhostFrame[];
}

export interface GhostPose {
  position: [number, number, number];
  yaw: number;
  pitch: number;
  /** True once the ghost has flown past the end of its recording. */
  finished: boolean;
}

const GHOST_KEY_PREFIX = "myworld-flight-ghost-v1";
const BEST_KEY_PREFIX = "myworld-flight-best-v1";
const MAX_FRAMES = 6000;
const MIN_MOVE = 0.05;
const MIN_INTERVAL = 0.05;

/** Derive the ghost storage key from the matching best-run key. */
export function ghostStorageKey(bestRunKey: string): string {
  if (bestRunKey.startsWith(BEST_KEY_PREFIX)) {
    return GHOST_KEY_PREFIX + bestRunKey.slice(BEST_KEY_PREFIX.length);
  }
  return `${GHOST_KEY_PREFIX}:${bestRunKey}`;
}

export function loadGhost(bestRunKey: string): GhostRecording | undefined {
  try {
    const raw = localStorage.getItem(ghostStorageKey(bestRunKey));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<GhostRecording>;
    if (!parsed || !Array.isArray(parsed.frames) || parsed.frames.length < 2) return undefined;
    const frames = parsed.frames
      .map((frame) => ({
        t: Number(frame?.t) || 0,
        x: Number(frame?.x) || 0,
        y: Number(frame?.y) || 0,
        z: Number(frame?.z) || 0,
        yaw: Number(frame?.yaw) || 0,
      }))
      .filter((frame) => Number.isFinite(frame.t));
    if (frames.length < 2) return undefined;
    return {
      key: bestRunKey,
      seconds: Number(parsed.seconds) || frames[frames.length - 1].t,
      collected: Number(parsed.collected) || 0,
      recordedAt: typeof parsed.recordedAt === "string" ? parsed.recordedAt : new Date().toISOString(),
      frames,
    };
  } catch {
    return undefined;
  }
}

export function saveGhost(recording: GhostRecording): boolean {
  try {
    localStorage.setItem(ghostStorageKey(recording.key), JSON.stringify(recording));
    return true;
  } catch {
    return false;
  }
}

export function clearGhost(bestRunKey: string): void {
  try {
    localStorage.removeItem(ghostStorageKey(bestRunKey));
  } catch {
    // ignore private-mode failures
  }
}

/**
 * Accumulates poses during a run. Uses a monotonic clock (performance.now)
 * so it stays accurate regardless of the 1 Hz display timer, and de-dupes
 * near-stationary frames to keep recordings small.
 */
export class GhostRecorder {
  private startAt = 0;
  private lastT = -Infinity;
  private last: GhostFrame | null = null;
  frames: GhostFrame[] = [];

  reset(now: number = performance.now()): void {
    this.startAt = now;
    this.lastT = -Infinity;
    this.last = null;
    this.frames = [];
  }

  push(player: PlayerSnapshot, now: number = performance.now()): void {
    if (this.frames.length >= MAX_FRAMES) return;
    const t = (now - this.startAt) / 1000;
    if (t < 0) return;
    const moved =
      !this.last ||
      Math.hypot(player.x - this.last.x, player.y - this.last.y, player.z - this.last.z) > MIN_MOVE ||
      Math.abs(player.yaw - this.last.yaw) > 0.08;
    if (this.last && t - this.lastT < MIN_INTERVAL && !moved) return;
    const frame: GhostFrame = { t, x: player.x, y: player.y, z: player.z, yaw: player.yaw };
    this.frames.push(frame);
    this.last = frame;
    this.lastT = t;
  }

  duration(): number {
    return this.frames.length ? this.frames[this.frames.length - 1].t : 0;
  }
}

function lerpAngle(a: number, b: number, k: number): number {
  const diff = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + diff * k;
}

/** Interpolate the ghost pose at time `t` (seconds since run start). */
export function sampleGhost(frames: GhostFrame[], t: number): GhostPose | null {
  if (frames.length < 2) return null;
  const first = frames[0];
  if (t <= first.t) {
    return { position: [first.x, first.y, first.z], yaw: first.yaw, pitch: 0, finished: false };
  }
  const last = frames[frames.length - 1];
  if (t >= last.t) {
    return { position: [last.x, last.y, last.z], yaw: last.yaw, pitch: 0, finished: true };
  }
  // Binary search for the segment containing t.
  let lo = 0;
  let hi = frames.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].t <= t) lo = mid;
    else hi = mid;
  }
  const a = frames[lo];
  const b = frames[hi];
  const span = b.t - a.t || 1;
  const k = Math.min(1, Math.max(0, (t - a.t) / span));
  const x = a.x + (b.x - a.x) * k;
  const y = a.y + (b.y - a.y) * k;
  const z = a.z + (b.z - a.z) * k;
  const horiz = Math.hypot(b.x - a.x, b.z - a.z);
  const pitch = horiz > 1e-4 ? Math.atan2(b.y - a.y, horiz) : 0;
  return { position: [x, y, z], yaw: lerpAngle(a.yaw, b.yaw, k), pitch, finished: false };
}

/**
 * Racing delta: project the player's current position onto the recorded track
 * and return how far ahead (negative) or behind (positive) they are in seconds,
 * versus where the ghost was when it passed the same point.
 */
export function ghostTimeDelta(
  frames: GhostFrame[],
  playerPos: { x: number; y: number; z: number },
  runTime: number,
): number | null {
  if (frames.length < 2) return null;
  let nearestT = frames[0].t;
  let nearestDist = Infinity;
  for (let i = 0; i < frames.length; i += 1) {
    const frame = frames[i];
    const dist =
      (playerPos.x - frame.x) ** 2 + (playerPos.y - frame.y) ** 2 + (playerPos.z - frame.z) ** 2;
    if (dist < nearestDist) {
      nearestDist = dist;
      nearestT = frame.t;
    }
  }
  return runTime - nearestT;
}
